const collectionCache = new Map();

class ZoobiSearchCollections extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this, { childList: true, subtree: true });
    this.onPriceChange = event => {
      if (event.target.matches('[data-native-search-price]')) {
        event.target.closest('facets-form-component')?.updateFilters();
      }
    };
    this.addEventListener('change', this.onPriceChange);
    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.removeEventListener('change', this.onPriceChange);
  }

  sync() {
    const url = new URL(location.href);
    const selected = url.searchParams.get('zs_collection') || '';
    this.querySelectorAll('[data-search-collection]').forEach(link => {
      const target = new URL(url);
      target.searchParams.delete('page');
      target.searchParams.delete('zs_page');
      const handle = link.dataset.searchCollection;
      if (handle) target.searchParams.set('zs_collection', handle);
      else target.searchParams.delete('zs_collection');
      link.href = target.href;
      if (handle === selected) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    this.querySelectorAll('form.facets__form').forEach(form => {
      let input = form.querySelector('[name="zs_collection"]');
      if (selected && !input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'zs_collection';
        form.append(input);
      }
      if (input) input.value = selected;
    });
    // Facet updates morph this section, and change the URL before the new cards arrive.
    // Run once per rendered URL; our own card mutations must not trigger another fetch.
    if (this.lastURL === url.href) return;
    this.lastURL = url.href;
    if (!selected) return;
    this.filter(url, selected);
  }

  async collectionIDs(path, signal) {
    if (collectionCache.has(path)) return collectionCache.get(path);
    const ids = new Set();
    for (let page = 1; ; page++) {
      const url = new URL(`${path.replace(/\/$/, '')}/products.json`, location.origin);
      url.searchParams.set('limit', '250');
      url.searchParams.set('page', String(page));
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error('Collection request failed');
      const { products } = await response.json();
      if (!Array.isArray(products)) throw new Error('Invalid collection response');
      products.forEach(product => ids.add(String(product.id)));
      if (products.length < 250) break;
    }
    collectionCache.set(path, ids);
    return ids;
  }

  async filter(url, selected) {
    this.controller.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    const results = this.querySelector('results-list');
    const grid = this.querySelector('.product-grid');
    const link = Array.from(this.querySelectorAll('[data-search-collection]')).find(item => item.dataset.searchCollection === selected);
    if (!results || !grid || !link) return;
    results.setAttribute('aria-busy', 'true');
    const status = document.createElement('p');
    status.className = 'zs-filter-status';
    status.setAttribute('role', 'status');
    status.textContent = this.dataset.loading;
    grid.before(status);
    try {
      const ids = await this.collectionIDs(link.dataset.collectionUrl, signal);
      const pages = Number(this.querySelector('[data-zs-page-count]')?.dataset.zsPageCount) || 1;
      const cards = [];
      const initialPage = Number(url.searchParams.get('page')) || 1;
      for (let page = 1; page <= pages; page++) {
        let root = this;
        if (page !== initialPage) {
          const pageURL = new URL(url);
          pageURL.searchParams.delete('zs_collection');
          pageURL.searchParams.delete('zs_page');
          pageURL.searchParams.set('page', String(page));
          pageURL.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(pageURL, { signal });
          if (!response.ok) throw new Error('Search request failed');
          root = new DOMParser().parseFromString(await response.text(), 'text/html');
        }
        root.querySelectorAll('.product-grid__item[data-product-id]').forEach(card => {
          if (ids.has(card.dataset.productId)) cards.push(card);
        });
      }
      if (signal.aborted || !this.isConnected) return;
      const size = Number(this.dataset.pageSize) || 12;
      const pageCount = Math.max(1, Math.ceil(cards.length / size));
      const current = Math.min(pageCount, Math.max(1, Number(url.searchParams.get('zs_page')) || 1));
      // Cards from separate server pages can reuse static block IDs. Keep form,
      // label and accessibility references unique when combining those pages.
      cards.forEach(card => {
        const replacements = new Map();
        card.querySelectorAll('[id]').forEach(element => {
          const original = element.id;
          const unique = `${original}--zs-${card.dataset.productId}`;
          replacements.set(original, unique);
          element.id = unique;
        });
        card.querySelectorAll('[for],[form],[aria-controls],[aria-labelledby],[aria-describedby]').forEach(element => {
          for (const name of ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby']) {
            const value = element.getAttribute(name);
            if (value) element.setAttribute(name, value.split(' ').map(id => replacements.get(id) || id).join(' '));
          }
        });
      });
      grid.replaceChildren(...cards.slice((current - 1) * size, current * size));
      this.querySelectorAll('.zs-empty').forEach(item => item.remove());
      if (!cards.length) {
        const empty = document.createElement('li');
        empty.className = 'zs-collection-empty';
        empty.append(this.querySelector('[data-search-empty]').content.cloneNode(true));
        grid.append(empty);
      }
      this.querySelectorAll('.products-count-wrapper [role="status"]').forEach(count => {
        count.textContent = this.dataset.countTemplate.replace('RESULT_COUNT', String(cards.length));
      });
      this.querySelectorAll('.facets__see-results').forEach(button => { button.textContent = this.dataset.seeTemplate.replace('RESULT_COUNT', String(cards.length)); });
      this.querySelectorAll('.pagination').forEach(nav => nav.remove());
      if (pageCount > 1) grid.after(this.pagination(url, current, pageCount));
      status.remove();
    } catch (error) {
      if (error.name !== 'AbortError') {
        grid.replaceChildren();
        status.textContent = this.dataset.error;
        status.setAttribute('role', 'alert');
      }
    } finally {
      if (!signal.aborted) results.removeAttribute('aria-busy');
    }
  }

  pagination(url, current, pages) {
    const nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('aria-label', this.dataset.pagination);
    const list = document.createElement('ul');
    list.className = 'pagination__list';
    const add = (number, label, arrow = false) => {
      const li = document.createElement('li');
      li.className = 'pagination__item';
      const disabled = number < 1 || number > pages;
      const item = document.createElement(disabled || number === current ? 'span' : 'a');
      item.className = `pagination__link ${arrow ? 'pagination__link--arrow' : 'pagination__link--page'}`;
      item.textContent = label;
      if (disabled) {
        item.classList.add('pagination__link--disabled');
        item.setAttribute('aria-disabled', 'true');
      } else if (number === current) {
        item.classList.add('pagination__link--current');
        item.setAttribute('aria-current', 'page');
      } else {
        const target = new URL(url);
        target.searchParams.delete('page');
        target.searchParams.set('zs_page', String(number));
        item.href = target.href;
      }
      if (arrow) item.setAttribute('aria-label', number < current ? this.dataset.previous : this.dataset.next);
      li.append(item);
      list.append(li);
    };
    add(current - 1, '‹', true);
    for (let page = 1; page <= pages; page++) {
      if (pages > 7 && page > 1 && page < pages && Math.abs(page - current) > 1) {
        if (page === 2 || page === current + 2) {
          const gap = document.createElement('li');
          gap.className = 'pagination__item';
          gap.textContent = '…';
          list.append(gap);
        }
        continue;
      }
      add(page, String(page));
    }
    add(current + 1, '›', true);
    nav.append(list);
    return nav;
  }
}

if (!customElements.get('zoobi-search-collections')) {
  customElements.define('zoobi-search-collections', ZoobiSearchCollections);
}
